import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Users, Search, Building2, MoreVertical, Eye, UserX, Shield, Activity,
  Clock, Filter, Download, Calendar, ChevronDown, ChevronUp, UserPlus, Upload,
  FileSpreadsheet, Plus, AlertCircle, Check, X, Loader2, CalendarIcon, Target, Pencil,
  Trash2, Crown, Gift, GraduationCap, LogIn, TrendingUp, Sparkles,
} from 'lucide-react';
import { RequestEnterpriseLicensesDialog } from '@/components/admin/RequestEnterpriseLicensesDialog';
import { useEnterpriseLicenseStats } from '@/hooks/useEnterpriseLicenseStats';
import { enterpriseLicenseGrantsAccess } from '@/lib/orgContract';
import { formatContractDate } from '@/lib/orgContract';
import { Textarea } from '@/components/ui/textarea';
import { format, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserManagement, type ManagedUser, ENTERPRISE_PLAN_ID } from '@/hooks/useUserManagement';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { useAuditorCohortIds } from '@/hooks/useAuditorCohortIds';
import {
  useUsersUtmAttribution, useUserUtmDetail, sourceLabel,
  DIRECT_SOURCE_LABEL, DIRECT_FILTER_VALUE,
} from '@/hooks/useUtmAttributionAdmin';
import { supabase } from '@/integrations/supabase/client';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from '@/components/ui/pagination';
import * as XLSX from 'xlsx';

interface ImportedUser {
  full_name: string;
  email: string;
  phone: string;
  role: string;
  position: string;
  area: string;
  matchedArea: string;
  originalArea: string;
  password: string;
  has_enterprise_license: boolean;
  pro_license: boolean;
  pro_start_date: string;
  pro_end_date: string;
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface ImportResult {
  email: string;
  success: boolean;
  error?: string;
  user_id?: string;
}

// Teléfono en formato internacional E.164 (+código de país). Obligatorio al crear
// o importar usuarios para que las notificaciones de WhatsApp funcionen.
const PHONE_E164_REGEX = /^\+[1-9]\d{7,14}$/;
const normalizePhone = (raw: string): string =>
  (raw || '')
    .normalize('NFKC')
    .replace(/[^\d+]/g, '')    // deja solo dígitos y '+'
    .replace(/(?!^)\+/g, '');  // '+' solo permitido al inicio
const isValidPhone = (p: string): boolean => PHONE_E164_REGEX.test(p);

async function getFunctionErrorMessage(error: any) {
  const ctx = error?.context;
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const parsed = await ctx.clone().json();
      if (parsed?.error || parsed?.message) return parsed.error || parsed.message;
    } catch {
      try {
        const text = await ctx.clone().text();
        if (text) return text;
      } catch { /* noop */ }
    }
  }
  return error?.message ?? 'No se pudo iniciar la sesión';
}

const actionLabels: Record<string, string> = {
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  content_view: 'Visualización de contenido',
  deliverable_submit: 'Entrega de actividad',
  diagnostic_complete: 'Diagnóstico completado',
  forum_post: 'Publicación en foro',
  project_update: 'Actualización de proyecto',
  ai_tool_use: 'Uso de herramienta IA',
  admin_action: 'Acción administrativa',
  calendar_event: 'Evento de calendario',
  user_created: 'Usuario creado',
  users_batch_created: 'Importación masiva',
  user_status_changed: 'Cambio de estado',
  user_role_changed: 'Cambio de rol',
  user_updated: 'Usuario actualizado',
  user_deleted: 'Usuario eliminado',
  pro_license_granted: 'Licencia Pro de cortesía activada',
  pro_license_revoked: 'Licencia Pro de cortesía revocada',
};



export default function UserManagementPage() {
  const queryClient = useQueryClient();
  const { currentRole, profile } = useAuth();
  const navigate = useNavigate();
  const {
    users, isLoadingUsers, organizations, cohorts, businessAreas,
    createUser, isCreatingUser, batchCreate, isBatchCreating,
    updateStatus, updateUser, isUpdatingUser, deleteUser, isDeletingUser,
    toggleEnterpriseLicense, isTogglingLicense,
    toggleProLicense, isTogglingProLicense,
    bulkToggleProLicense, isBulkTogglingProLicense,
    bulkSetOrganization, isBulkSettingOrganization,
    isSuperAdmin, isAdmin, callerOrgId,
  } = useUserManagement();

  const isCompanyAdmin = isAdmin && !isSuperAdmin;
  // Auditor: rol de SOLO LECTURA. Se ocultan todas las acciones de mutación.
  const isAuditor = currentRole === 'auditor';
  const { cohortIds: auditorCohortIds } = useAuditorCohortIds();
  // La información de origen (UTM / Fuente) es exclusiva del super administrador.
  const { map: utmMap, sources: utmSources } = useUsersUtmAttribution(isSuperAdmin);
  const callerOrgName = useMemo(
    () => organizations.find((o) => o.id === callerOrgId)?.name || '',
    [organizations, callerOrgId]
  );

  // Pagination
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  // searchInput = lo que se escribe; searchTerm = valor aplicado (con debounce)
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedCohort, setSelectedCohort] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedUtmSource, setSelectedUtmSource] = useState<string>('all');

  // Audit log filters
  const [selectedActionType, setSelectedActionType] = useState<string>('all');
  const [selectedUserForLogs, setSelectedUserForLogs] = useState<string>('all');
  const [selectedCompanyForLogs, setSelectedCompanyForLogs] = useState<string>('all');
  const [logSearchTerm, setLogSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // User detail dialog
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [logsExpanded, setLogsExpanded] = useState<Record<string, boolean>>({});
  const {
    data: userUtmDetail, error: userUtmDetailError,
  } = useUserUtmDetail(isUserDetailOpen && isSuperAdmin ? selectedUser?.id ?? null : null);

  // Create user dialog
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  // Cohortes a asignar si el nuevo usuario es Auditor (se asignan al crear).
  const [newAuditorCohortIds, setNewAuditorCohortIds] = useState<string[]>([]);
  const [auditorCohortSearch, setAuditorCohortSearch] = useState('');
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    phone: '',
    organization_id: isCompanyAdmin ? (callerOrgId || '') : '',
    roles: ['participant'] as string[],
    position: '',
    area: '',
    password: '',
  });

  // Edit user dialog
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editUser, setEditUser] = useState<{
    user_id: string;
    full_name: string;
    phone: string;
    email: string;
    organization_id: string;
    roles: string[];
    position: string;
    area: string;
    cohort_ids: string[];
    new_password: string;
  } | null>(null);
  // Cohortes a auditar del usuario en edición (tabla cohort_auditors, separada
  // de cohort_ids que es inscripción como participante).
  const [editAuditorCohortIds, setEditAuditorCohortIds] = useState<string[]>([]);
  const [editAuditorCohortSearch, setEditAuditorCohortSearch] = useState('');

  // Import dialog
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importedUsers, setImportedUsers] = useState<ImportedUser[]>([]);
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'creating' | 'results'>('upload');
  const [importCohortId, setImportCohortId] = useState<string>('');
  const [importCompanyId, setImportCompanyId] = useState<string>(isCompanyAdmin ? (callerOrgId || '') : '');
  const [importRole, setImportRole] = useState<string>('participant');
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importQuotaWarnings, setImportQuotaWarnings] = useState<string[]>([]);

  // Delete confirmation
  const [userToDelete, setUserToDelete] = useState<ManagedUser | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Pro courtesy license dialog (with optional dates)
  const [proLicenseDialog, setProLicenseDialog] = useState<{ user: ManagedUser; mode: 'grant' | 'revoke' } | null>(null);
  const [proLicenseReason, setProLicenseReason] = useState('');
  const [proLicenseStart, setProLicenseStart] = useState<string>('');
  const [proLicenseEnd, setProLicenseEnd] = useState<string>('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialog, setBulkDialog] = useState<null | { mode: 'grant' | 'revoke' }>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  // Asignar / quitar empresa en lote
  const [orgBulkDialog, setOrgBulkDialog] = useState<null | { mode: 'assign' | 'remove' }>(null);
  const [orgBulkTarget, setOrgBulkTarget] = useState('');

  // Helper: detect manual Pro grant (provider 'manual' or null = courtesy)
  const hasManualProLicense = (u: ManagedUser) =>
    u.subscriptions.some(
      (s) =>
        s.plan_type === 'subscription' &&
        s.status === 'active' &&
        (s.provider === 'manual' || s.provider === null)
    );
  const hasStripeLiveProLicense = (u: ManagedUser) =>
    u.subscriptions.some(
      (s) =>
        s.plan_type === 'subscription' &&
        s.status === 'active' &&
        s.provider === 'stripe' &&
        s.livemode === true
    );

  const handleConfirmProLicense = async () => {
    if (!proLicenseDialog) return;
    const { user, mode } = proLicenseDialog;
    if (mode === 'grant' && proLicenseReason.trim().length < 5) {
      toast({
        title: 'Motivo requerido',
        description: 'Indica un motivo (mínimo 5 caracteres) para otorgar la licencia Pro de cortesía.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await toggleProLicense({
        user_id: user.id,
        enable: mode === 'grant',
        reason: proLicenseReason.trim() || undefined,
        start_date: mode === 'grant' && proLicenseStart ? proLicenseStart : null,
        end_date: mode === 'grant' && proLicenseEnd ? proLicenseEnd : null,
      });
      setProLicenseDialog(null);
      setProLicenseReason('');
      setProLicenseStart('');
      setProLicenseEnd('');
    } catch {
      /* toast handled in hook */
    }
  };

  // Bulk handlers
  const proSubscriptionRow = (u: ManagedUser) =>
    u.subscriptions.find(s => s.plan_type === 'subscription' && s.status === 'active' && (s.provider === 'manual' || s.provider === null));

  const formatLicenseEnd = (u: ManagedUser): string => {
    const row = proSubscriptionRow(u);
    if (!row) return '';
    if (!row.current_period_end) return 'Ilimitada';
    // current_period_end se guarda como medianoche UTC del día elegido (ver
    // toggle_pro_license en manage-users). Formatear con new Date(iso) directo
    // reinterpreta esa medianoche UTC en la zona local del navegador y corre
    // la fecha un día hacia atrás en zonas UTC-N (ej. Colombia). Se toma solo
    // la parte de fecha y se construye como medianoche LOCAL para evitar el corrimiento.
    const dateOnly = row.current_period_end.slice(0, 10);
    return `hasta ${format(new Date(`${dateOnly}T00:00:00`), 'dd MMM yyyy', { locale: es })}`;
  };

  const handleBulkConfirm = async () => {
    if (!bulkDialog) return;
    if (bulkDialog.mode === 'grant' && bulkReason.trim().length < 5) {
      toast({ title: 'Motivo requerido', description: 'Indica un motivo (mínimo 5 caracteres).', variant: 'destructive' });
      return;
    }
    try {
      await bulkToggleProLicense({
        user_ids: Array.from(selectedIds),
        enable: bulkDialog.mode === 'grant',
        reason: bulkReason.trim() || undefined,
        start_date: bulkDialog.mode === 'grant' && bulkStart ? bulkStart : null,
        end_date: bulkDialog.mode === 'grant' && bulkEnd ? bulkEnd : null,
      });
      setBulkDialog(null);
      setBulkReason('');
      setBulkStart('');
      setBulkEnd('');
      setSelectedIds(new Set());
    } catch { /* toast handled in hook */ }
  };

  const handleOrgBulkConfirm = async () => {
    if (!orgBulkDialog) return;
    if (orgBulkDialog.mode === 'assign' && !orgBulkTarget) {
      toast({ title: 'Selecciona una empresa', variant: 'destructive' });
      return;
    }
    try {
      await bulkSetOrganization({
        user_ids: Array.from(selectedIds),
        organization_id: orgBulkDialog.mode === 'assign' ? orgBulkTarget : null,
      });
      setOrgBulkDialog(null);
      setOrgBulkTarget('');
      setSelectedIds(new Set());
    } catch { /* toast handled in hook */ }
  };

  // Visible cohorts for filter dropdown (respects role + selected company)
  const visibleCohorts = useMemo(() => {
    let list = cohorts as any[];
    // Auditor: solo sus cohortes asignadas (la RLS también expone públicas).
    if (isAuditor) {
      list = list.filter((c) => auditorCohortIds.includes(c.id));
    }
    if (isCompanyAdmin) {
      list = list.filter((c) => c.organization_id === callerOrgId);
    } else if (selectedCompany === 'none') {
      list = list.filter((c) => !c.organization_id);
    } else if (selectedCompany !== 'all') {
      list = list.filter((c) => c.organization_id === selectedCompany);
    }
    return list;
  }, [cohorts, isCompanyAdmin, callerOrgId, selectedCompany, isAuditor, auditorCohortIds]);

  // Reset cohort filter if it's no longer visible
  useEffect(() => {
    if (selectedCohort !== 'all' && !visibleCohorts.some((c: any) => c.id === selectedCohort)) {
      setSelectedCohort('all');
    }
  }, [visibleCohorts, selectedCohort]);

  // Filter users
  const filteredUsers = useMemo(() => {
    const result = users.filter((user) => {
      const matchesSearch =
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompany =
        selectedCompany === 'all' ||
        (selectedCompany === 'none' ? !user.organization_id : user.organization_id === selectedCompany);
      const matchesCohort =
        selectedCohort === 'all' || user.cohorts.some((c) => c.id === selectedCohort);
      const matchesRole = selectedRole === 'all' || user.roles.includes(selectedRole);
      const matchesStatus =
        selectedStatus === 'all' || user.status === selectedStatus;
      const matchesUtmSource =
        selectedUtmSource === 'all' ||
        (selectedUtmSource === DIRECT_FILTER_VALUE
          ? !utmMap.get(user.id) || utmMap.get(user.id)!.isDirect || !utmMap.get(user.id)!.source
          : utmMap.get(user.id)?.source === selectedUtmSource);

      return matchesSearch && matchesCompany && matchesCohort && matchesRole && matchesStatus && matchesUtmSource;
    });
    // Reset page when filters change
    return result;
  }, [users, searchTerm, selectedCompany, selectedCohort, selectedRole, selectedStatus, selectedUtmSource, utmMap]);

  // Reset page when filters change
  const filterKey = `${searchTerm}-${selectedCompany}-${selectedCohort}-${selectedRole}-${selectedStatus}-${selectedUtmSource}`;
  useEffect(() => { setCurrentPage(1); }, [filterKey]);

  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage, PAGE_SIZE]);

  // Keep selection consistent with current filter set
  useEffect(() => {
    const visible = new Set(filteredUsers.map(u => u.id));
    setSelectedIds(prev => {
      const next = new Set<string>();
      prev.forEach(id => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filteredUsers]);

  const visibleIds = useMemo(() => filteredUsers.map(u => u.id), [filteredUsers]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = !allVisibleSelected && visibleIds.some(id => selectedIds.has(id));
  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) visibleIds.forEach(id => next.add(id));
      else visibleIds.forEach(id => next.delete(id));
      return next;
    });
  };
  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  // Audit logs pagination + server-side filters
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(20);

  const auditFilters = useMemo(() => ({
    action: selectedActionType,
    actorUserId: selectedUserForLogs,
    organizationId: isCompanyAdmin ? (callerOrgId || 'all') : selectedCompanyForLogs,
    search: logSearchTerm,
    dateFrom,
    dateTo,
  }), [selectedActionType, selectedUserForLogs, selectedCompanyForLogs, logSearchTerm, dateFrom, dateTo, isCompanyAdmin, callerOrgId]);

  // Reset to page 1 when filters change
  const auditFilterKey = JSON.stringify({ ...auditFilters, dateFrom: dateFrom?.toISOString(), dateTo: dateTo?.toISOString(), auditPageSize });
  useEffect(() => { setAuditPage(1); }, [auditFilterKey]);

  const auditLogsResult = useAuditLogs({
    page: auditPage,
    pageSize: auditPageSize,
    filters: auditFilters,
    enabled: isSuperAdmin || isAdmin,
  });
  const filteredLogs = auditLogsResult.data?.logs || [];
  const auditTotal = auditLogsResult.data?.total || 0;
  const auditTotalPages = Math.max(1, Math.ceil(auditTotal / auditPageSize));
  const isLoadingLogs = auditLogsResult.isLoading;

  const handleImpersonateUser = async (target: ManagedUser) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast({ title: 'Error', description: 'No hay sesión activa', variant: 'destructive' });
        return;
      }
      localStorage.setItem('_impersonation_admin_session', JSON.stringify({
        access_token: sessionData.session.access_token,
        refresh_token: sessionData.session.refresh_token,
      }));
      localStorage.setItem('_impersonation_target', JSON.stringify({
        id: target.id,
        name: target.full_name,
        email: target.email,
      }));
      localStorage.setItem('_impersonation_in_progress', '1');

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { targetUserId: target.id },
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      if (error || !data?.token_hash) {
        localStorage.removeItem('_impersonation_admin_session');
        localStorage.removeItem('_impersonation_target');
        localStorage.removeItem('_impersonation_in_progress');
        toast({ title: 'Error', description: error ? await getFunctionErrorMessage(error) : 'No se pudo iniciar la sesión', variant: 'destructive' });
        return;
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: 'magiclink',
      });
      if (otpError) {
        localStorage.removeItem('_impersonation_admin_session');
        localStorage.removeItem('_impersonation_target');
        localStorage.removeItem('_impersonation_in_progress');
        toast({ title: 'Error', description: otpError.message, variant: 'destructive' });
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      localStorage.removeItem('_impersonation_admin_session');
      localStorage.removeItem('_impersonation_target');
      localStorage.removeItem('_impersonation_in_progress');
      toast({ title: 'Error inesperado', description: String(err), variant: 'destructive' });
    }
  };

  const handleViewUser = (user: ManagedUser) => {
    setSelectedUser(user);
    setIsUserDetailOpen(true);
  };

  const handleEditUser = async (user: ManagedUser) => {
    setEditUser({
      user_id: user.id,
      full_name: user.full_name,
      phone: user.phone || '',
      email: user.email,
      organization_id: user.organization_id || '',
      roles: user.roles.length > 0 ? [...user.roles] : ['participant'],
      position: user.position || '',
      area: user.area || '',
      cohort_ids: user.cohorts.map((c) => c.id),
      new_password: '',
    });
    setEditAuditorCohortSearch('');
    setEditAuditorCohortIds([]);
    setIsEditUserOpen(true);
    if (user.roles.includes('auditor')) {
      const { data, error } = await (supabase as any)
        .from('cohort_auditors')
        .select('cohort_id')
        .eq('user_id', user.id);
      if (!error) {
        setEditAuditorCohortIds((data || []).map((r: any) => r.cohort_id));
      }
    }
  };

  const toggleLogExpanded = (logId: string) => {
    setLogsExpanded((prev) => ({ ...prev, [logId]: !prev[logId] }));
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline'; label: string }> = {
      participant: { variant: 'secondary', label: 'Participante' },
      admin: { variant: 'default', label: 'Admin Empresa' },
      mentor: { variant: 'outline', label: 'Mentor' },
      super_admin: { variant: 'default', label: 'Super Admin' },
      auditor: { variant: 'outline', label: 'Auditor' },
    };
    const config = variants[role] || { variant: 'secondary', label: role };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string | null) => {
    if (status === 'active') {
      return (
        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
          Activo
        </Badge>
      );
    }
    if (status === 'suspended') {
      return (
        <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          Suspendido
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground">
        Inactivo
      </Badge>
    );
  };


  // Create user handler
  const handleCreateUser = async () => {
    if (!newUser.full_name || !newUser.email) {
      toast({ title: 'Error', description: 'Nombre y email son requeridos', variant: 'destructive' });
      return;
    }
    const phoneNorm = normalizePhone(newUser.phone);
    if (phoneNorm && !isValidPhone(phoneNorm)) {
      toast({ title: 'Error', description: 'El teléfono debe estar en formato internacional (ej. +573001234567)', variant: 'destructive' });
      return;
    }
    if (newUser.roles.length === 0) {
      toast({ title: 'Error', description: 'Selecciona al menos un rol', variant: 'destructive' });
      return;
    }
    if (!isSuperAdmin && (newUser.roles.includes('super_admin') || newUser.roles.includes('auditor'))) {
      toast({ title: 'Permiso denegado', description: 'Solo un Super Admin puede asignar los roles de Super Admin o Auditor', variant: 'destructive' });
      return;
    }
    const creatingAuditor = newUser.roles.includes('auditor');
    if (creatingAuditor && newAuditorCohortIds.length === 0) {
      toast({ title: 'Error', description: 'Un Auditor debe tener al menos una cohorte asignada.', variant: 'destructive' });
      return;
    }

    const orgId = isCompanyAdmin ? callerOrgId : newUser.organization_id;

    if (newUser.roles.includes('admin') && !orgId) {
      toast({ title: 'Error', description: 'El rol Admin Empresa requiere seleccionar una empresa', variant: 'destructive' });
      return;
    }
    const primaryRole = newUser.roles[0];

    const result = await createUser({
      email: newUser.email,
      full_name: newUser.full_name,
      phone: phoneNorm,
      role: primaryRole,
      organization_id: orgId || undefined,
      position: newUser.position || undefined,
      area: newUser.area || undefined,
      password: newUser.password || undefined,
    });

    // If multiple roles, update the user to set all roles
    if (result?.user_id && newUser.roles.length > 1) {
      try {
        await updateUser({
          user_id: result.user_id,
          roles: newUser.roles,
        });
      } catch {
        // Primary role was already set, additional roles failed silently
      }
    }

    // Si es Auditor, asignarle las cohortes elegidas (tabla cohort_auditors).
    if (result?.user_id && creatingAuditor && newAuditorCohortIds.length > 0) {
      try {
        const rows = newAuditorCohortIds.map((cid) => ({ cohort_id: cid, user_id: result.user_id }));
        const { error } = await (supabase as any).from('cohort_auditors').insert(rows);
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['cohort-management', 'auditors'] });
      } catch (err: any) {
        toast({ title: 'Usuario creado, pero falló asignar cohortes', description: err?.message || 'Asignalas manualmente desde Gestión de Cohortes.', variant: 'destructive' });
      }
    }

    setIsCreateUserOpen(false);
    setNewAuditorCohortIds([]);
    setAuditorCohortSearch('');
    setNewUser({
      full_name: '', email: '', phone: '',
      organization_id: isCompanyAdmin ? (callerOrgId || '') : '',
      roles: ['participant'], position: '', area: '', password: '',
    });
  };

  // Edit user handler
  const handleSaveEditUser = async () => {
    if (!editUser) return;

    if (editUser.roles.length === 0) {
      toast({ title: 'Error', description: 'Selecciona al menos un rol', variant: 'destructive' });
      return;
    }

    const editPhoneNorm = normalizePhone(editUser.phone);
    if (editPhoneNorm && !isValidPhone(editPhoneNorm)) {
      toast({ title: 'Error', description: 'El teléfono debe estar en formato internacional (ej. +573001234567)', variant: 'destructive' });
      return;
    }

    if (!isSuperAdmin && (editUser.roles.includes('super_admin') || editUser.roles.includes('auditor'))) {
      toast({ title: 'Permiso denegado', description: 'Solo un Super Admin puede asignar los roles de Super Admin o Auditor', variant: 'destructive' });
      return;
    }

    const editingAuditor = editUser.roles.includes('auditor');
    if (editingAuditor && editAuditorCohortIds.length === 0) {
      toast({ title: 'Error', description: 'Un Auditor debe tener al menos una cohorte asignada.', variant: 'destructive' });
      return;
    }

    await updateUser({
      user_id: editUser.user_id,
      full_name: editUser.full_name,
      phone: editPhoneNorm,
      organization_id: editUser.organization_id || null,
      roles: editUser.roles,
      position: editUser.position,
      area: editUser.area,
      cohort_ids: editUser.cohort_ids,
      new_password: editUser.new_password || undefined,
    });

    // Sincroniza cohort_auditors (reemplaza el set completo) si es Auditor.
    if (editingAuditor) {
      try {
        const { error: delError } = await (supabase as any)
          .from('cohort_auditors')
          .delete()
          .eq('user_id', editUser.user_id);
        if (delError) throw delError;
        const rows = editAuditorCohortIds.map((cid) => ({ cohort_id: cid, user_id: editUser.user_id }));
        const { error: insError } = await (supabase as any).from('cohort_auditors').insert(rows);
        if (insError) throw insError;
        queryClient.invalidateQueries({ queryKey: ['cohort-management', 'auditors'] });
      } catch (err: any) {
        toast({ title: 'Usuario actualizado, pero falló guardar las cohortes a auditar', description: err?.message || 'Asignalas manualmente desde Gestión de Cohortes.', variant: 'destructive' });
      }
    }

    setIsEditUserOpen(false);
    setEditUser(null);
  };

  const toggleCohort = async (cohortId: string) => {
    if (!editUser) return;

    // If adding a cohort, validate quota
    if (!editUser.cohort_ids.includes(cohortId)) {
      const cohort = cohorts.find((c) => c.id === cohortId);
      const userOrgId = editUser.organization_id;

      if (cohort && userOrgId && (cohort as any).program_id) {
        try {
          // Check organization_program_quotas
          const { data: quota } = await supabase
            .from('organization_program_quotas')
            .select('contracted_qty')
            .eq('organization_id', userOrgId)
            .eq('program_id', (cohort as any).program_id)
            .maybeSingle();

          if (quota) {
            // Count current participants in cohorts of this org+program
            const programCohorts = cohorts.filter(
              (c) => (c as any).program_id === (cohort as any).program_id
            );
            const programCohortIds = programCohorts.map((c) => c.id);

            // Count users from this org already in these cohorts
            let usedCount = 0;
            users.forEach((u) => {
              if (u.organization_id === userOrgId) {
                const inProgramCohorts = u.cohorts.some((uc) => programCohortIds.includes(uc.id));
                if (inProgramCohorts) usedCount++;
              }
            });

            // Also count if the current user is already counted
            const userAlreadyCounted = editUser.cohort_ids.some((cid) => programCohortIds.includes(cid));
            if (!userAlreadyCounted && usedCount >= quota.contracted_qty) {
              toast({
                title: 'Sin cupos disponibles',
                description: `La organización ha usado ${usedCount}/${quota.contracted_qty} cupos para esta ruta`,
                variant: 'destructive',
              });
              return;
            }
          }
        } catch {
          // If quota check fails, allow assignment
        }
      }
    }

    setEditUser((prev) => {
      if (!prev) return prev;
      const ids = prev.cohort_ids.includes(cohortId)
        ? prev.cohort_ids.filter((id) => id !== cohortId)
        : [...prev.cohort_ids, cohortId];
      return { ...prev, cohort_ids: ids };
    });
  };

  const handleToggleEnterpriseLicense = async (userId: string, enable: boolean) => {
    if (enable && enterpriseLicenseStats) {
      const { used, max } = enterpriseLicenseStats;
      if (max === null || max === 0) {
        toast({
          title: 'Sin licencias contratadas',
          description: 'La organización no tiene licencias Enterprise contratadas. Contacta al Super Admin.',
          variant: 'destructive',
        });
        return;
      }
      if (used >= max) {
        toast({
          title: 'Límite alcanzado',
          description: `La organización ya consumió las ${max} licencias Enterprise contratadas.`,
          variant: 'destructive',
        });
        return;
      }
    }
    await toggleEnterpriseLicense({ user_id: userId, enable });
  };

  // Normalize text for area matching
  const normalizeText = (text: string) =>
    text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  const matchArea = (rawArea: string): { matched: string; exact: boolean } => {
    if (!rawArea) return { matched: '', exact: true };
    const norm = normalizeText(rawArea);
    // Exact match
    const exact = businessAreas.find(a => normalizeText(a.name) === norm);
    if (exact) return { matched: exact.name, exact: true };
    // Partial inclusion
    const partial = businessAreas.find(a =>
      normalizeText(a.name).includes(norm) || norm.includes(normalizeText(a.name))
    );
    if (partial) return { matched: partial.name, exact: false };
    return { matched: '', exact: false };
  };

  // File upload for import
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', raw: false, cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(sheet);

        // Filter out completely empty rows
        const nonEmptyRows = rows.filter((row) => {
          const vals = Object.values(row).map((v) => (v || '').toString().trim());
          return vals.some((v) => v.length > 0);
        });


        // Strips invisible chars (zero-width, NBSP, BOM) that Excel commonly pastes
        // alongside emails — Supabase Auth rejects these with "invalid format".
        const sanitizeEmail = (raw: string): string =>
          raw
            .normalize('NFKC')
            // u200B-u200D zero-width / u2060 word joiner / uFEFF BOM / u00A0 NBSP
            .replace(/[​-‍⁠﻿ ]/g, '')
            .replace(/[​-‍﻿ ]/g, '')
            .trim()
            .toLowerCase();
        const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Excel suele convertir la columna Teléfono a número: "573001234567"
        // termina mostrándose/exportándose como "5,73104E+11". Reconstruimos los
        // dígitos antes de validar en vez de rechazar el valor.
        const recoverPhone = (raw: string): string => {
          const v = raw.trim();
          if (!v) return '';
          const sci = v.replace(',', '.').match(/^(\d(?:\.\d+)?)[eE]\+?(\d+)$/);
          if (sci) {
            const [, mantissa, expStr] = sci;
            const exp = parseInt(expStr, 10);
            const [intPart, decPart = ''] = mantissa.split('.');
            const digits = (intPart + decPart).padEnd(intPart.length + exp, '0');
            return `+${digits.slice(0, intPart.length + exp)}`;
          }
          return v;
        };

        const parsed: ImportedUser[] = nonEmptyRows.map((row) => {
          const errors: string[] = [];
          const warnings: string[] = [];
          const g = (keys: string[]) => {
            for (const k of keys) {
              for (const rk of Object.keys(row)) {
                if (rk.toLowerCase().trim() === k.toLowerCase()) return (row[rk] || '').toString().trim();
              }
            }
            return '';
          };
          const name = g(['nombre', 'name', 'full_name']);
          const email = sanitizeEmail(g(['email', 'correo']));
          const phone = normalizePhone(recoverPhone(g(['telefono', 'teléfono', 'celular', 'whatsapp', 'phone', 'movil', 'móvil', 'cel'])));
          const position = g(['cargo', 'position', 'puesto']);
          const rawArea = g(['área', 'area']);
          const password = g(['contraseña', 'password', 'clave']);
          // La plantilla descargable (ver downloadTemplate más abajo) usa la columna
          // "Tipo de Licencia" con valores Pro/Enterprise/vacío desde 2026-05-29, pero
          // este parser seguía buscando el formato viejo ("Licencia Enterprise": Sí/No)
          // y nunca lo detectaba — se mantiene ese formato viejo solo por compatibilidad
          // con plantillas ya descargadas antes de este fix.
          const licenseTypeRaw = g(['tipo de licencia', 'tipo_licencia']).toLowerCase();
          const legacyEnterpriseRaw = g(['licencia enterprise', 'licencia_enterprise']).toLowerCase();
          const hasEnterprise = licenseTypeRaw === 'enterprise' || ['sí', 'si', 'yes', '1', 'true'].includes(legacyEnterpriseRaw);
          const wantsPro = licenseTypeRaw === 'pro';
          // Pro de cortesía solo la puede otorgar un Super Admin (toggle_pro_license
          // lo rechaza con 403 para cualquier otro rol) — si un Admin Empresa importa
          // con "Pro", se crea el usuario sin licencia y se avisa en vez de fallar en silencio.
          const proAllowed = wantsPro && isSuperAdmin;
          if (wantsPro && !isSuperAdmin) {
            warnings.push('Licencia Pro solo puede asignarla un Super Admin: se creará sin licencia');
          }
          const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
          let proStartDate = '';
          let proEndDate = '';
          if (proAllowed) {
            const startRaw = g(['fecha inicio', 'fecha_inicio']);
            const endRaw = g(['fecha fin', 'fecha_fin']);
            if (startRaw && !DATE_ONLY_REGEX.test(startRaw)) {
              warnings.push(`Fecha Inicio "${startRaw}" no tiene formato YYYY-MM-DD, se ignora`);
            } else {
              proStartDate = startRaw;
            }
            if (endRaw && !DATE_ONLY_REGEX.test(endRaw)) {
              warnings.push(`Fecha Fin "${endRaw}" no tiene formato YYYY-MM-DD, se ignora`);
            } else {
              proEndDate = endRaw;
            }
          }

          if (!name) errors.push('Nombre vacío');
          if (!email || !EMAIL_REGEX.test(email)) errors.push('Email inválido');
          if (!phone) warnings.push('Sin teléfono: no recibirá notificaciones por WhatsApp');
          else if (!isValidPhone(phone)) errors.push('Teléfono inválido (usar formato internacional, ej. +573001234567)');

          const areaMatch = matchArea(rawArea);
          if (rawArea && !areaMatch.matched) {
            warnings.push(`Área "${rawArea}" no encontrada`);
          } else if (rawArea && !areaMatch.exact) {
            warnings.push(`Área "${rawArea}" → "${areaMatch.matched}"`);
          }

          if (!password) {
            warnings.push('Sin contraseña: se enviará email de invitación');
          }

          return {
            full_name: name,
            email,
            phone,
            role: importRole,
            position,
            area: areaMatch.matched || rawArea,
            matchedArea: areaMatch.matched,
            originalArea: rawArea,
            password,
            has_enterprise_license: hasEnterprise,
            pro_license: proAllowed,
            pro_start_date: proStartDate,
            pro_end_date: proEndDate,
            isValid: errors.length === 0,
            errors,
            warnings,
          };
        });

        setImportedUsers(parsed);
        setImportStep('preview');
        validateQuotas(parsed);
      } catch {
        toast({ title: 'Error', description: 'No se pudo leer el archivo', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Validate quotas for preview alerts
  const validateQuotas = async (parsedUsers: ImportedUser[]) => {
    const warnings: string[] = [];
    const targetOrgId = isCompanyAdmin ? callerOrgId : importCompanyId;

    // Validate enterprise licenses
    if (targetOrgId) {
      const enterpriseCount = parsedUsers.filter(u => u.has_enterprise_license && u.isValid).length;
      if (enterpriseCount > 0) {
        const { data: org } = await supabase
          .from('organizations')
          .select('max_users')
          .eq('id', targetOrgId)
          .single();
        const maxUsers = org?.max_users ?? 0;
        if (maxUsers === 0) {
          warnings.push(`La organización no tiene licencias Enterprise contratadas. No se pueden asignar ${enterpriseCount} licencias.`);
        } else {
          const currentLicenses = users.filter(u => u.organization_id === targetOrgId && u.has_enterprise_license).length;
          if (currentLicenses + enterpriseCount > maxUsers) {
            warnings.push(`Se excede el límite de licencias Enterprise: ${currentLicenses} actuales + ${enterpriseCount} nuevas = ${currentLicenses + enterpriseCount}, máximo contratado: ${maxUsers}`);
          }
        }
      }
    }

    // Validate program quotas if cohort selected
    if (importCohortId && targetOrgId) {
      const cohort = cohorts.find(c => c.id === importCohortId);
      if (cohort?.program_id) {
        const { data: quota } = await supabase
          .from('organization_program_quotas')
          .select('contracted_qty')
          .eq('organization_id', targetOrgId)
          .eq('program_id', cohort.program_id)
          .maybeSingle();

        if (quota) {
          const programCohortIds = cohorts.filter(c => c.program_id === cohort.program_id).map(c => c.id);
          let usedCount = 0;
          users.forEach(u => {
            if (u.organization_id === targetOrgId && u.cohorts.some(uc => programCohortIds.includes(uc.id))) {
              usedCount++;
            }
          });
          const newValid = parsedUsers.filter(u => u.isValid).length;
          if (usedCount + newValid > quota.contracted_qty) {
            warnings.push(`Se excede el cupo de la ruta: ${usedCount} actuales + ${newValid} nuevos = ${usedCount + newValid}, cupo contratado: ${quota.contracted_qty}`);
          }
        }
      }
    }

    setImportQuotaWarnings(warnings);
  };

  // Extrae el mensaje de error de una respuesta de supabase.functions.invoke —
  // usado tanto para Enterprise como para Pro al otorgar licencias en bloque tras la importación.
  const parseInvokeError = async (error: any, data: any): Promise<string | null> => {
    if (error) {
      try {
        const ctx: any = error.context;
        if (ctx?.body) {
          const parsed = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
          if (parsed?.error) return parsed.error;
        } else if (typeof ctx?.text === 'function') {
          const txt = await ctx.text();
          try { const parsed = JSON.parse(txt); if (parsed?.error) return parsed.error; }
          catch { if (txt) return txt; }
        }
      } catch { /* ignore */ }
      return error.message;
    }
    return data?.error || null;
  };

  const handleCreateImportedUsers = async () => {
    const targetOrgId = isCompanyAdmin ? callerOrgId : importCompanyId;

    setImportStep('creating');

    const validUsers = importedUsers.filter((u) => u.isValid);
    const result = await batchCreate({
      users: validUsers.map((u) => ({
        email: u.email,
        full_name: u.full_name,
        phone: u.phone,
        role: importRole,
        position: u.position || undefined,
        area: u.area || undefined,
        password: u.password || undefined,
      })),
      organization_id: targetOrgId || undefined,
      cohort_id: importCohortId || undefined,
    });

    // Activate enterprise licenses with explicit quota check.
    // Pre-fetch remaining quota so we don't fire N edge function errors when the org is full.
    const results = result?.results || [];
    const enterpriseCandidates = validUsers
      .filter(u => u.has_enterprise_license)
      .map(u => ({ email: u.email, created: results.find((r: ImportResult) => r.email === u.email && r.success && r.user_id) }))
      .filter(c => c.created?.user_id);

    let licenseSkipped: { email: string; reason: string }[] = [];
    let licenseFailed: { email: string; error: string }[] = [];

    if (enterpriseCandidates.length > 0 && targetOrgId) {
      const { data: org } = await supabase
        .from('organizations')
        .select('max_users')
        .eq('id', targetOrgId)
        .single();
      const maxUsers = org?.max_users ?? 0;

      let available = Infinity;
      if (maxUsers === 0) {
        available = 0;
      } else {
        // Count currently active enterprise subs in the org (server-side, authoritative)
        const { data: orgProfiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('organization_id', targetOrgId);
        const orgUserIds = (orgProfiles || []).map(p => p.id);
        if (orgUserIds.length > 0) {
          const { count } = await supabase
            .from('user_subscriptions')
            .select('id', { count: 'exact', head: true })
            .in('user_id', orgUserIds)
            .eq('plan_id', ENTERPRISE_PLAN_ID)
            .eq('status', 'active');
          available = Math.max(0, maxUsers - (count || 0));
        } else {
          available = maxUsers;
        }
      }

      const toAssign = enterpriseCandidates.slice(0, available);
      licenseSkipped = enterpriseCandidates.slice(available).map(c => ({
        email: c.email,
        reason: maxUsers === 0
          ? 'La organización no tiene licencias Enterprise contratadas.'
          : `Se alcanzó el cupo contratado (${maxUsers} licencias).`,
      }));

      // Direct invokes — bypasses the toggleEnterpriseLicense mutation's per-call error toast
      for (const c of toAssign) {
        const { data: tData, error: tError } = await supabase.functions.invoke('manage-users', {
          body: { action: 'toggle_enterprise_license', user_id: c.created!.user_id, enable: true },
        });
        const errMsg = await parseInvokeError(tError, tData);
        if (errMsg) licenseFailed.push({ email: c.email, error: errMsg });
      }

      // Refresh license-related caches now that the batch is done
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['org-enterprise-counts'] });
    }

    // Grant Pro courtesy licenses for candidates marked in the import (only ever
    // true when the importer is Super Admin — see parsing in handleFileUpload).
    const proCandidates = validUsers
      .filter(u => u.pro_license)
      .map(u => ({
        email: u.email,
        start_date: u.pro_start_date || null,
        end_date: u.pro_end_date || null,
        created: results.find((r: ImportResult) => r.email === u.email && r.success && r.user_id),
      }))
      .filter(c => c.created?.user_id);

    const proFailed: { email: string; error: string }[] = [];
    if (proCandidates.length > 0) {
      for (const c of proCandidates) {
        const { data: pData, error: pError } = await supabase.functions.invoke('manage-users', {
          body: {
            action: 'toggle_pro_license',
            user_id: c.created!.user_id,
            enable: true,
            reason: 'Importación masiva de usuarios',
            start_date: c.start_date,
            end_date: c.end_date,
          },
        });
        const errMsg = await parseInvokeError(pError, pData);
        if (errMsg) proFailed.push({ email: c.email, error: errMsg });
      }
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
    }

    setImportResults(results);
    setImportStep('results');

    // Single, clear summary toast for license outcomes (no edge function noise)
    if (licenseSkipped.length > 0 || licenseFailed.length > 0) {
      const total = licenseSkipped.length + licenseFailed.length;
      const reasonSample = licenseSkipped[0]?.reason || licenseFailed[0]?.error || '';
      toast({
        title: `${total} usuario(s) sin licencia Enterprise`,
        description: `Los usuarios fueron creados, pero no recibieron licencia Enterprise. ${reasonSample} Ajusta el cupo de la organización y reasigna desde la lista de usuarios.`,
        variant: 'destructive',
      });
    }
    if (proFailed.length > 0) {
      toast({
        title: `${proFailed.length} usuario(s) sin licencia Pro`,
        description: `Los usuarios fueron creados, pero no recibieron la licencia Pro de cortesía. ${proFailed[0].error}`,
        variant: 'destructive',
      });
    }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Nombre', 'Email', 'Teléfono', 'Cargo', 'Área', 'Contraseña', 'Tipo de Licencia', 'Fecha Inicio', 'Fecha Fin'],
      ['Juan Pérez', 'juan@empresa.com', '+573001234567', 'Gerente', 'Tecnología', 'Pass1234!', 'Enterprise', '', ''],
      ['Ana López', 'ana@empresa.com', '+573007654321', 'Directora', 'RRHH', 'Pass5678!', 'Pro', '2026-01-01', '2026-12-31'],
      ['Luis García', 'luis@empresa.com', '+573009876543', 'Analista', 'Operaciones', 'Pass9012!', 'Pro', '', ''],
    ]);

    // Fuerza formato Texto en Teléfono (C) y en las fechas (H, I), incluyendo
    // filas vacías: sin esto Excel convierte "+573001234567" en el número
    // 5,73104E+11 y reinterpreta "2026-01-01" según el formato regional.
    const TEXT_COLS = [2, 7, 8]; // C, H, I
    const TEXT_FORMAT_ROWS = 500;
    for (let r = 1; r <= TEXT_FORMAT_ROWS; r++) {
      for (const c of TEXT_COLS) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) ws[ref] = { t: 's', v: '' };
        ws[ref].t = 's';
        if (ws[ref].v != null) ws[ref].v = String(ws[ref].v);
        ws[ref].w = ws[ref].v as string;
        ws[ref].z = '@';
      }
    }
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    range.e.r = Math.max(range.e.r, TEXT_FORMAT_ROWS);
    range.e.c = Math.max(range.e.c, 8);
    ws['!ref'] = XLSX.utils.encode_range(range);

    ws['!cols'] = [
      { wch: 22 }, // Nombre
      { wch: 28 }, // Email
      { wch: 20 }, // Teléfono
      { wch: 18 }, // Cargo
      { wch: 18 }, // Área
      { wch: 14 }, // Contraseña
      { wch: 16 }, // Tipo de Licencia
      { wch: 14 }, // Fecha Inicio
      { wch: 14 }, // Fecha Fin
    ];

    // Hint row: dejar fechas vacías = licencia ilimitada
    XLSX.utils.sheet_add_aoa(ws, [['Nota: Teléfono OPCIONAL; si se incluye, usar formato internacional (+código de país, ej. +573001234567). Las columnas Teléfono, Fecha Inicio y Fecha Fin vienen formateadas como Texto: escríbelas tal cual, sin convertirlas a número o fecha. Tipo de Licencia: Pro / Enterprise / vacío. Fechas en formato YYYY-MM-DD; vacías = Ilimitada.']], { origin: 'A6' });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    XLSX.writeFile(wb, 'plantilla_usuarios.xlsx');
  };


  const handleSetStatus = async (user: ManagedUser, newStatus: string) => {
    await updateStatus({ user_id: user.id, status: newStatus });
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    await deleteUser(userToDelete.id);
    setUserToDelete(null);
  };

  // Enterprise license stats for company admin: mismo contador (consulta a la base)
  // que usa la tarjeta "Plan de la empresa" en Configuración.
  const { data: orgContractMap } = useQuery({
    queryKey: ['org-contract-map'],
    queryFn: async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, plan_tier, contract_start_date, contract_end_date');
      return new Map((data ?? []).map((o: any) => [o.id, o]));
    },
    staleTime: 5 * 60 * 1000,
  });
  const isLicenseContractExpired = (orgId: string | null | undefined) => {
    const o: any = orgId ? orgContractMap?.get(orgId) : null;
    if (!o) return false;
    return !enterpriseLicenseGrantsAccess({ hasLicense: true, planTier: o.plan_tier, start: o.contract_start_date, end: o.contract_end_date });
  };
  const { data: licenseStatsData } = useEnterpriseLicenseStats(
    isCompanyAdmin && callerOrgId ? callerOrgId : null,
  );
  const enterpriseLicenseStats = useMemo(() => {
    if (!isCompanyAdmin || !callerOrgId || !licenseStatsData) return null;
    return { used: licenseStatsData.used, max: licenseStatsData.max };
  }, [isCompanyAdmin, callerOrgId, licenseStatsData]);

  // Stats
  const stats = useMemo(() => ({
    totalUsers: filteredUsers.length,
    activeUsers: filteredUsers.filter((u) => u.status === 'active').length,
    companies: isCompanyAdmin ? 1 : new Set(filteredUsers.map((u) => u.organization_id).filter(Boolean)).size,
  }), [filteredUsers, isCompanyAdmin]);

  // Export users to Excel
  const handleExportUsers = useCallback(() => {
    const exportData = filteredUsers.map(u => ({
      Nombre: u.full_name,
      Email: u.email,
      Empresa: u.organization_name || '—',
      Rol: u.roles.join(', ') || 'participant',
      Cargo: u.position || '—',
      Área: u.area || '—',
      Cohortes: u.cohorts.map(c => c.name).join(', ') || '—',
      Estado: u.status === 'active' ? 'Activo' : 'Inactivo',
      'Licencia Enterprise': u.has_enterprise_license ? 'Sí' : 'No',
      ...(isSuperAdmin ? { Fuente: sourceLabel(utmMap.get(u.id)) } : {}),
      'Último acceso': u.last_login ? format(new Date(u.last_login), 'dd/MM/yyyy HH:mm') : 'Nunca',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = Object.keys(exportData[0] || {}).map(k => ({ wch: Math.max(k.length, 15) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    XLSX.writeFile(wb, `usuarios_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }, [filteredUsers, utmMap]);

  const validImportCount = importedUsers.filter((u) => u.isValid).length;
  const invalidImportCount = importedUsers.filter((u) => !u.isValid).length;

  if (isLoadingUsers) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Gestión de Usuarios
          </h1>
          <p className="text-muted-foreground mt-1">
            {isCompanyAdmin
              ? `Administra los usuarios de ${callerOrgName}`
              : 'Administra todos los usuarios de la plataforma'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportUsers} className="gap-2" disabled={filteredUsers.length === 0}>
            <Download className="w-4 h-4" />
            Exportar
          </Button>
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setIsImportDialogOpen(true)} className="gap-2">
              <Upload className="w-4 h-4" />
              Importar Usuarios
            </Button>
          )}
          {!isAuditor && (
            <Button onClick={() => setIsCreateUserOpen(true)} className="gap-2">
              <UserPlus className="w-4 h-4" />
              Crear Usuario
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalUsers}</p>
                <p className="text-sm text-muted-foreground">Total Usuarios</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Activity className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeUsers}</p>
                <p className="text-sm text-muted-foreground">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {!isCompanyAdmin && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.companies}</p>
                  <p className="text-sm text-muted-foreground">Empresas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {isCompanyAdmin && enterpriseLicenseStats && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Target className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {enterpriseLicenseStats.used}
                    <span className="text-base font-normal text-muted-foreground">/{enterpriseLicenseStats.max}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">Licencias Enterprise</p>
                </div>
              </div>
              {enterpriseLicenseStats.max === 0 && (
                <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Sin licencias contratadas
                </p>
              )}
              {enterpriseLicenseStats.max > 0 && enterpriseLicenseStats.used >= enterpriseLicenseStats.max && (
                <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Límite alcanzado
                </p>
              )}
              {licenseStatsData?.contractEndDate && (
                <p className={`text-xs mt-2 ${licenseStatsData.contract.isExpired ? 'text-destructive' : licenseStatsData.contract.isExpiringSoon ? 'text-warning' : 'text-muted-foreground'}`}>
                  {licenseStatsData.contract.isExpired
                    ? `Contrato vencido el ${formatContractDate(licenseStatsData.contractEndDate)}: la empresa está en plan Gratuito`
                    : licenseStatsData.contract.isExpiringSoon
                      ? `Vence en ${licenseStatsData.contract.daysRemaining} días (${formatContractDate(licenseStatsData.contractEndDate)})`
                      : `Vigente hasta ${formatContractDate(licenseStatsData.contractEndDate)}`}
                </p>
              )}
              {(enterpriseLicenseStats.max === 0 || licenseStatsData?.contract.isExpiringSoon || licenseStatsData?.contract.isExpired ||
                enterpriseLicenseStats.used >= enterpriseLicenseStats.max) && (
                <Button
                  size="sm"
                  className="mt-3 w-full gap-2"
                  onClick={() => setUpgradeOpen(true)}
                >
                  <Sparkles className="w-4 h-4" />
                  Contratar licencias Enterprise
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <RequestEnterpriseLicensesDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        orgName={organizations.find((o) => o.id === callerOrgId)?.name}
        used={enterpriseLicenseStats?.used ?? 0}
        max={enterpriseLicenseStats?.max ?? 0}
      />

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <Activity className="w-4 h-4" />
            Log de Auditoría
          </TabsTrigger>
        </TabsList>

        {/* === USERS TAB === */}
        <TabsContent value="users" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {!isCompanyAdmin && (
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger className="w-full md:w-48">
                      <Building2 className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Empresa" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4} avoidCollisions={false}>
                      <SelectItem value="all" className="py-1 text-sm">Todas las empresas</SelectItem>
                      <SelectItem value="none" className="py-1 text-sm">Sin empresa</SelectItem>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id} className="py-1 text-sm">
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                  <SelectTrigger className="w-full md:w-56">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Cohorte" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} avoidCollisions={false}>
                    <SelectItem value="all" className="py-1 text-sm">Todas las cohortes</SelectItem>
                    {visibleCohorts.map((c: any) => (
                      <SelectItem key={c.id} value={c.id} className="py-1 text-sm">
                        {c.name}
                        {isSuperAdmin && (
                          <span className="text-muted-foreground ml-1">
                            · {c.organization_name || 'Pública'}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-full md:w-40">
                    <Shield className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los roles</SelectItem>
                    <SelectItem value="participant">Participante</SelectItem>
                    <SelectItem value="admin">Admin Empresa</SelectItem>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="auditor">Auditor</SelectItem>
                    {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                  </SelectContent>
                </Select>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full md:w-36">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                    <SelectItem value="suspended">Suspendidos</SelectItem>
                  </SelectContent>
                </Select>
                {isSuperAdmin && (
                  <Select value={selectedUtmSource} onValueChange={setSelectedUtmSource}>
                    <SelectTrigger className="w-full md:w-44">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Fuente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las fuentes</SelectItem>
                      <SelectItem value={DIRECT_FILTER_VALUE}>{DIRECT_SOURCE_LABEL}</SelectItem>
                      {utmSources.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>Lista de Usuarios</CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-3">
                <span>
                  Mostrando {Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredUsers.length)}-{Math.min(currentPage * PAGE_SIZE, filteredUsers.length)} de {filteredUsers.length} usuarios
                </span>
                <span className="flex items-center gap-2">
                  Por página:
                  <Select
                    value={String(PAGE_SIZE)}
                    onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="h-7 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 50].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>
              </CardDescription>
              {isSuperAdmin && selectedIds.size > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                  <span className="text-sm font-medium">{selectedIds.size} seleccionado(s)</span>
                  <Button size="sm" variant="default" className="gap-2" onClick={() => setBulkDialog({ mode: 'grant' })}>
                    <Gift className="w-4 h-4" /> Asignar Pro
                  </Button>
                  <Button size="sm" variant="destructive" className="gap-2" onClick={() => setBulkDialog({ mode: 'revoke' })}>
                    <UserX className="w-4 h-4" /> Quitar Pro
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => setOrgBulkDialog({ mode: 'assign' })}>
                    <Building2 className="w-4 h-4" /> Asignar empresa
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={() => setOrgBulkDialog({ mode: 'remove' })}>
                    <Building2 className="w-4 h-4" /> Quitar empresa
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                    Limpiar selección
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {isSuperAdmin && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                          onCheckedChange={(c) => toggleAllVisible(!!c)}
                          aria-label="Seleccionar todos los visibles"
                        />
                      </TableHead>
                    )}
                    <TableHead>Usuario</TableHead>
                    {!isCompanyAdmin && <TableHead>Empresa</TableHead>}
                    <TableHead>Rol</TableHead>
                    {isSuperAdmin && <TableHead>Fuente</TableHead>}
                    <TableHead>Licencia</TableHead>
                    <TableHead>Cohorte</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Último acceso</TableHead>
                    <TableHead>Fecha de creación</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow key={user.id}>
                      {isSuperAdmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(user.id)}
                            onCheckedChange={(c) => toggleOne(user.id, !!c)}
                            aria-label={`Seleccionar ${user.full_name}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-medium text-sm">
                            {user.full_name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{user.full_name}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      {!isCompanyAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            {user.organization_name || '—'}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((r, i) => (
                            <span key={i}>{getRoleBadge(r)}</span>
                          ))}
                          {user.roles.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <Badge variant="outline">{sourceLabel(utmMap.get(user.id))}</Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap gap-1">
                            {user.has_enterprise_license && (isLicenseContractExpired(user.organization_id)
                              ? <Badge variant="outline" className="text-xs text-muted-foreground" title="La licencia se conserva y vuelve a funcionar al renovar">Contrato vencido</Badge>
                              : <Badge variant="default" className="text-xs">Enterprise</Badge>
                            )}
                            {user.has_pro_subscription && (
                              <Badge
                                variant="secondary"
                                className="text-xs"
                                title={hasManualProLicense(user) ? 'Pro (cortesía – asignada por Super Admin)' : 'Pro (suscripción Stripe)'}
                              >
                                {hasManualProLicense(user) ? 'Pro · Cortesía' : 'Pro'}
                              </Badge>
                            )}
                            {!user.has_enterprise_license && !user.has_pro_subscription && (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </div>
                          {hasManualProLicense(user) && (
                            <span className="text-xs text-muted-foreground">{formatLicenseEnd(user)}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.cohorts.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.cohorts.map((c) => (
                              <Badge key={c.id} variant="outline" className="text-xs">
                                {c.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell>
                        {user.last_login ? (
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            {format(new Date(user.last_login), 'dd MMM, HH:mm', { locale: es })}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Nunca</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.created_at ? (
                          <span className="text-sm">
                            {format(new Date(user.created_at), 'dd MMM yyyy', { locale: es })}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewUser(user)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalle
                            </DropdownMenuItem>
                            {!isAuditor && (<>
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/user-tracking?user=${user.id}`)}>
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Ver actividad
                            </DropdownMenuItem>
                            {isSuperAdmin && user.id !== profile?.id && (
                              <DropdownMenuItem onClick={() => handleImpersonateUser(user)}>
                                <LogIn className="w-4 h-4 mr-2" />
                                Iniciar como usuario
                              </DropdownMenuItem>
                            )}
                            {user.status !== 'active' && (
                              <DropdownMenuItem
                                className="text-green-600"
                                onClick={() => handleSetStatus(user, 'active')}
                              >
                                <Activity className="w-4 h-4 mr-2" />
                                Activar
                              </DropdownMenuItem>
                            )}
                            {user.status !== 'inactive' && (
                              <DropdownMenuItem
                                className="text-muted-foreground"
                                onClick={() => handleSetStatus(user, 'inactive')}
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Desactivar
                              </DropdownMenuItem>
                            )}
                            {user.status !== 'suspended' && (
                              <DropdownMenuItem
                                className="text-orange-600"
                                onClick={() => handleSetStatus(user, 'suspended')}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Suspender
                              </DropdownMenuItem>
                            )}
                            {!user.has_enterprise_license && (
                              <DropdownMenuItem
                                className="text-primary"
                                disabled={
                                  isTogglingLicense ||
                                  (isCompanyAdmin && enterpriseLicenseStats
                                    ? enterpriseLicenseStats.max === 0 ||
                                      enterpriseLicenseStats.used >= enterpriseLicenseStats.max
                                    : false)
                                }
                                onClick={() => handleToggleEnterpriseLicense(user.id, true)}
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Otorgar licencia Enterprise
                              </DropdownMenuItem>
                            )}
                            {user.has_enterprise_license && (
                              <DropdownMenuItem
                                className="text-muted-foreground"
                                disabled={isTogglingLicense}
                                onClick={() => handleToggleEnterpriseLicense(user.id, false)}
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Revocar licencia Enterprise
                              </DropdownMenuItem>
                            )}
                            {isSuperAdmin && !hasStripeLiveProLicense(user) && !hasManualProLicense(user) && (
                              <DropdownMenuItem
                                className="text-amber-600"
                                onClick={() => { setProLicenseReason(''); setProLicenseStart(''); setProLicenseEnd(''); setProLicenseDialog({ user, mode: 'grant' }); }}
                              >
                                <Gift className="w-4 h-4 mr-2" />
                                Otorgar Pro (cortesía)
                              </DropdownMenuItem>
                            )}
                            {isSuperAdmin && hasManualProLicense(user) && (
                              <DropdownMenuItem
                                className="text-amber-700"
                                onClick={() => { setProLicenseReason(''); setProLicenseDialog({ user, mode: 'revoke' }); }}
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Revocar Pro de cortesía
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setUserToDelete(user)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                            </>)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={(isCompanyAdmin ? 8 : 9) + (isSuperAdmin ? 1 : 0) + (isAuditor ? 0 : 1)} className="text-center py-8 text-muted-foreground">
                        No se encontraron usuarios
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </p>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let page: number;
                        if (totalPages <= 5) {
                          page = i + 1;
                        } else if (currentPage <= 3) {
                          page = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          page = totalPages - 4 + i;
                        } else {
                          page = currentPage - 2 + i;
                        }
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === AUDIT TAB === */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nombre o descripción..."
                    value={logSearchTerm}
                    onChange={(e) => setLogSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-4 flex-wrap">
                  <Select value={selectedActionType} onValueChange={setSelectedActionType}>
                    <SelectTrigger className="w-full md:w-56">
                      <Activity className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Tipo de acción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las acciones</SelectItem>
                      {Object.entries(actionLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={selectedUserForLogs} onValueChange={setSelectedUserForLogs}>
                    <SelectTrigger className="w-full md:w-56">
                      <Users className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {!isCompanyAdmin && (
                    <Select value={selectedCompanyForLogs} onValueChange={(value) => {
                      setSelectedCompanyForLogs(value);
                      setSelectedUserForLogs('all');
                    }}>
                      <SelectTrigger className="w-full md:w-48">
                        <Building2 className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las empresas</SelectItem>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Date From */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full md:w-[160px] justify-start text-left font-normal',
                          !dateFrom && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-background border border-border z-50" align="start">
                      <CalendarComponent mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus locale={es} />
                    </PopoverContent>
                  </Popover>

                  {/* Date To */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full md:w-[160px] justify-start text-left font-normal',
                          !dateTo && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-background border border-border z-50" align="start">
                      <CalendarComponent mode="single" selected={dateTo} onSelect={setDateTo} initialFocus locale={es} />
                    </PopoverContent>
                  </Popover>

                  {(dateFrom || dateTo) && (
                    <Button variant="ghost" size="icon" onClick={() => { setDateFrom(undefined); setDateTo(undefined); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Log de Auditoría y Trazabilidad
              </CardTitle>
              <CardDescription>
                Registro completo de acciones en la plataforma ({auditTotal} registros)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredLogs.map((log) => (
                    <div key={log.id} className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted">
                            <Activity className="w-4 h-4 text-primary" />
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{log.actor_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {actionLabels[log.action] || log.action}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{log.description}</p>
                            {logsExpanded[log.id] && log.metadata && (
                              <div className="mt-2 p-3 rounded-md bg-muted text-sm">
                                <p className="font-medium mb-1">Metadata:</p>
                                <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {format(new Date(log.created_at), 'dd MMM yyyy, HH:mm', { locale: es })}
                          </span>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleLogExpanded(log.id)}>
                            {logsExpanded[log.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredLogs.length === 0 && (
                    <p className="text-center py-8 text-muted-foreground">No hay registros de auditoría</p>
                  )}

                  {auditTotal > 0 && (
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Filas por página:</span>
                        <Select value={String(auditPageSize)} onValueChange={(v) => setAuditPageSize(Number(v))}>
                          <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[10, 20, 50, 100].map((n) => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="ml-2">
                          Página {auditPage} de {auditTotalPages} · {auditTotal} registros
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={auditPage <= 1}
                          onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={auditPage >= auditTotalPages}
                          onClick={() => setAuditPage((p) => Math.min(auditTotalPages, p + 1))}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* === CREATE USER DIALOG === */}
      <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Crear Usuario
            </DialogTitle>
            <DialogDescription>Agrega un nuevo usuario a la plataforma</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input
                placeholder="Ej: María García"
                value={newUser.full_name}
                onChange={(e) => setNewUser((prev) => ({ ...prev, full_name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="maria@empresa.com"
                value={newUser.email}
                onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono (WhatsApp)</Label>
              <Input
                type="tel"
                placeholder="+573001234567"
                value={newUser.phone}
                onChange={(e) => setNewUser((prev) => ({ ...prev, phone: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Opcional. Formato internacional con código de país (ej. +57 para Colombia).</p>
            </div>
            {isCompanyAdmin ? (
              <div className="space-y-2">
                <Label>Empresa{newUser.roles.includes('admin') ? ' *' : ''}</Label>
                <Input value={callerOrgName} disabled />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Empresa{newUser.roles.includes('admin') ? ' *' : ''}</Label>
                <Select value={newUser.organization_id || 'none'} onValueChange={(val) => setNewUser((prev) => ({ ...prev, organization_id: val === 'none' ? '' : val, position: '' }))}>
                  <SelectTrigger className={newUser.roles.includes('admin') && !newUser.organization_id ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Selecciona empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {!newUser.roles.includes('admin') && <SelectItem value="none">Sin empresa</SelectItem>}
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newUser.roles.includes('admin') && !newUser.organization_id && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Obligatorio para el rol Admin Empresa
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Roles *</Label>
              <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
                {[
                  { value: 'participant', label: 'Participante' },
                  { value: 'admin', label: 'Admin Empresa' },
                  ...(!isCompanyAdmin ? [{ value: 'mentor', label: 'Mentor' }] : []),
                  ...(isSuperAdmin ? [{ value: 'auditor', label: 'Auditor (solo lectura)' }] : []),
                  ...(isSuperAdmin ? [{ value: 'super_admin', label: 'Super Admin' }] : []),
                ].map((roleOption) => (
                  <div key={roleOption.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`create-role-${roleOption.value}`}
                      checked={newUser.roles.includes(roleOption.value)}
                      onCheckedChange={(checked) => {
                        setNewUser((prev) => {
                          const roles = checked
                            ? [...prev.roles.filter((r) => r !== roleOption.value), roleOption.value]
                            : prev.roles.filter((r) => r !== roleOption.value);
                          return { ...prev, roles };
                        });
                      }}
                    />
                    <Label htmlFor={`create-role-${roleOption.value}`} className="text-sm cursor-pointer">{roleOption.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            {newUser.roles.includes('auditor') && (
              <div className="space-y-2 p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  <Label className="font-semibold">Cohortes a AUDITAR (solo lectura) *</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Esto define a qué cohortes tiene acceso de solo lectura como Auditor (distinto de estar inscrito como
                  participante). El auditor solo verá información de las cohortes que le asignes acá.
                </p>
                <Input
                  placeholder="Buscar cohorte por nombre o empresa..."
                  value={auditorCohortSearch}
                  onChange={(e) => setAuditorCohortSearch(e.target.value)}
                />
                <div className="max-h-48 overflow-y-auto space-y-1 p-2 rounded-lg border bg-background">
                  {cohorts
                    .filter((c) => {
                      const q = auditorCohortSearch.toLowerCase();
                      return !q || c.name.toLowerCase().includes(q) || (c.organization_name || '').toLowerCase().includes(q);
                    })
                    .map((c) => (
                      <label key={c.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox
                          checked={newAuditorCohortIds.includes(c.id)}
                          onCheckedChange={(checked) => {
                            setNewAuditorCohortIds((prev) =>
                              checked ? [...prev, c.id] : prev.filter((id) => id !== c.id)
                            );
                          }}
                        />
                        <span className="flex-1">
                          {c.name}
                          {c.organization_name && <span className="text-muted-foreground ml-1">· {c.organization_name}</span>}
                        </span>
                      </label>
                    ))}
                  {cohorts.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-3">No hay cohortes disponibles.</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{newAuditorCohortIds.length} cohorte(s) seleccionada(s) para auditar</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Cargo</Label>
              <Input value={newUser.position} onChange={(e) => setNewUser((prev) => ({ ...prev, position: e.target.value }))} placeholder="Ej: Gerente de Innovación" />
            </div>
            <div className="space-y-2">
              <Label>Área</Label>
              <Select value={newUser.area || 'none'} onValueChange={(val) => setNewUser((prev) => ({ ...prev, area: val === 'none' ? '' : val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona área (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin área</SelectItem>
                  {businessAreas.map((a) => (
                    <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input
                type="password"
                placeholder="Mínimo 6 caracteres (opcional)"
                value={newUser.password}
                onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Si se deja vacío, se enviará un email de invitación.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateUserOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateUser} disabled={isCreatingUser}>
              {isCreatingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === EDIT USER DIALOG === */}
      <Dialog open={isEditUserOpen} onOpenChange={(open) => { setIsEditUserOpen(open); if (!open) setEditUser(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Editar Usuario
            </DialogTitle>
            <DialogDescription>Modifica los datos del usuario</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input
                  value={editUser.full_name}
                  onChange={(e) => setEditUser((prev) => prev ? { ...prev, full_name: e.target.value } : prev)}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={editUser.email} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono (WhatsApp)</Label>
                <Input
                  type="tel"
                  placeholder="+573001234567"
                  value={editUser.phone}
                  onChange={(e) => setEditUser((prev) => prev ? { ...prev, phone: e.target.value } : prev)}
                />
                <p className="text-xs text-muted-foreground">Opcional. Formato internacional con código de país (ej. +57 para Colombia).</p>
              </div>
              {isCompanyAdmin ? (
                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <Input value={callerOrgName} disabled className="bg-muted" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <Select value={editUser.organization_id || 'none'} onValueChange={(val) => setEditUser((prev) => prev ? { ...prev, organization_id: val === 'none' ? '' : val, position: '' } : prev)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin empresa</SelectItem>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(isSuperAdmin ||
                (!editUser.roles.includes('super_admin') && !editUser.roles.includes('auditor'))) && (
                <div className="space-y-2">
                  <Label>Roles</Label>
                  <div className="border rounded-md p-2 space-y-1">
                    {[
                      { value: 'participant', label: 'Participante' },
                      { value: 'admin', label: 'Admin Empresa' },
                      ...(isSuperAdmin || !isCompanyAdmin ? [{ value: 'mentor', label: 'Mentor' }] : []),
                      ...(isSuperAdmin ? [{ value: 'auditor', label: 'Auditor (solo lectura)' }] : []),
                      ...(isSuperAdmin ? [{ value: 'super_admin', label: 'Super Admin' }] : []),
                    ].map((r) => (
                      <label key={r.value} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox
                          checked={editUser.roles.includes(r.value)}
                          onCheckedChange={(checked) => {
                            setEditUser((prev) => {
                              if (!prev) return prev;
                              const newRoles = checked
                                ? [...prev.roles.filter((x) => x !== r.value), r.value]
                                : prev.roles.filter((x) => x !== r.value);
                              return { ...prev, roles: newRoles };
                            });
                          }}
                        />
                        <span>{r.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {editUser.roles.includes('auditor') && (
                <div className="space-y-2 p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-primary" />
                    <Label className="font-semibold">Cohortes a AUDITAR (solo lectura) *</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Esto NO es inscripción como participante (eso es "Cohortes asignadas", más abajo). Acá defines a qué
                    cohortes tiene acceso de solo lectura como Auditor. El auditor solo verá información de las cohortes que le asignes acá.
                  </p>
                  <Input
                    placeholder="Buscar cohorte por nombre o empresa..."
                    value={editAuditorCohortSearch}
                    onChange={(e) => setEditAuditorCohortSearch(e.target.value)}
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1 p-2 rounded-lg border bg-background">
                    {cohorts
                      .filter((c) => {
                        const q = editAuditorCohortSearch.toLowerCase();
                        return !q || c.name.toLowerCase().includes(q) || (c.organization_name || '').toLowerCase().includes(q);
                      })
                      .map((c) => (
                        <label key={c.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted cursor-pointer text-sm">
                          <Checkbox
                            checked={editAuditorCohortIds.includes(c.id)}
                            onCheckedChange={(checked) => {
                              setEditAuditorCohortIds((prev) =>
                                checked ? [...prev, c.id] : prev.filter((id) => id !== c.id)
                              );
                            }}
                          />
                          <span className="flex-1">
                            {c.name}
                            {c.organization_name && <span className="text-muted-foreground ml-1">· {c.organization_name}</span>}
                          </span>
                        </label>
                      ))}
                    {cohorts.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-3">No hay cohortes disponibles.</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{editAuditorCohortIds.length} cohorte(s) seleccionada(s) para auditar</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Input value={editUser.position || ''} onChange={(e) => setEditUser((prev) => prev ? { ...prev, position: e.target.value } : prev)} placeholder="Ej: Gerente de Innovación" />
              </div>
              <div className="space-y-2">
                <Label>Área</Label>
                <Select value={editUser.area || 'none'} onValueChange={(val) => setEditUser((prev) => prev ? { ...prev, area: val === 'none' ? '' : val } : prev)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona área" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin área</SelectItem>
                    {businessAreas.map((a) => (
                      <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Enterprise License Switch */}
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
                <div>
                  <Label className="text-sm font-medium">Licencia Enterprise</Label>
                  <p className="text-xs text-muted-foreground">Asignar licencia Enterprise a este usuario</p>
                </div>
                {(() => {
                  const currentHas = users.find((u) => u.id === editUser.user_id)?.has_enterprise_license || false;
                  const noQuota = isCompanyAdmin && enterpriseLicenseStats
                    && (enterpriseLicenseStats.max === 0
                        || enterpriseLicenseStats.used >= enterpriseLicenseStats.max);
                  // Allow turning OFF even when at limit; block turning ON when no quota.
                  const disableSwitch = isTogglingLicense || (!currentHas && !!noQuota);
                  return (
                    <Switch
                      checked={currentHas}
                      disabled={disableSwitch}
                      onCheckedChange={(checked) => handleToggleEnterpriseLicense(editUser.user_id, checked)}
                    />
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Nueva contraseña</Label>
                <Input
                  type="password"
                  placeholder="Dejar vacío para no cambiar"
                  value={editUser.new_password}
                  onChange={(e) => setEditUser((prev) => prev ? { ...prev, new_password: e.target.value } : prev)}
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">Mínimo 6 caracteres. Solo completa este campo si deseas cambiar la contraseña del usuario.</p>
              </div>
              <div className="space-y-2">
                <Label>Cohortes asignadas (inscripción como participante)</Label>
                {editUser.cohort_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {editUser.cohort_ids.map((cid) => {
                      const c = cohorts.find((ch) => ch.id === cid);
                      return c ? (
                        <Badge key={cid} variant="secondary" className="gap-1">
                          {c.name}
                          <button onClick={() => toggleCohort(cid)} className="ml-1 hover:text-destructive">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                  {(() => {
                    const userOrgId = editUser.organization_id;
                    // Super admins can assign to any cohort regardless of org
                    const availableCohorts = isSuperAdmin
                      ? cohorts
                      : cohorts.filter(c =>
                          c.is_public || !c.organization_id || c.organization_id === userOrgId
                        );
                    const enterpriseCohorts = availableCohorts.filter(c => c.organization_id && (isSuperAdmin || c.organization_id === userOrgId));
                    const publicCohorts = availableCohorts.filter(c => c.is_public || !c.organization_id);

                    const formatDateRange = (start: string | null, end: string | null) => {
                      if (!start) return null;
                      const fmt = (d: string) => new Date(d).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
                      const yr = end ? new Date(end).getFullYear() : new Date(start).getFullYear();
                      return end ? `${fmt(start)} – ${fmt(end)}, ${yr}` : `${fmt(start)}, ${yr}`;
                    };

                    const renderCohort = (c: typeof cohorts[0]) => (
                      <label key={c.id} className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox
                          checked={editUser.cohort_ids.includes(c.id)}
                          onCheckedChange={() => toggleCohort(c.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{c.name}</span>
                            {c.status && (
                              <Badge variant="outline" className="text-xs shrink-0">{c.status}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            {c.program_name && <span>{c.program_name}</span>}
                            {c.program_name && formatDateRange(c.start_date, c.end_date) && <span>·</span>}
                            {formatDateRange(c.start_date, c.end_date) && <span>{formatDateRange(c.start_date, c.end_date)}</span>}
                          </div>
                        </div>
                      </label>
                    );

                    if (availableCohorts.length === 0) {
                      return <p className="text-sm text-muted-foreground text-center py-2">No hay cohortes disponibles</p>;
                    }

                    return (
                      <>
                        {enterpriseCohorts.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground px-2 pt-1">Cohortes de empresa</p>
                            {enterpriseCohorts.map(renderCohort)}
                          </>
                        )}
                        {publicCohorts.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground px-2 pt-1">Cohortes abiertas</p>
                            {publicCohorts.map(renderCohort)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditUserOpen(false); setEditUser(null); }}>Cancelar</Button>
            <Button onClick={handleSaveEditUser} disabled={isUpdatingUser}>
              {isUpdatingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === IMPORT DIALOG === */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
        setIsImportDialogOpen(open);
        if (!open) { setImportedUsers([]); setImportStep('upload'); setImportCohortId(''); setImportRole('participant'); setImportResults([]); setImportQuotaWarnings([]); setImportCompanyId(isCompanyAdmin ? (callerOrgId || '') : ''); }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Importar Usuarios
            </DialogTitle>
            <DialogDescription>
              {importStep === 'upload' && 'Carga un archivo Excel y selecciona empresa/cohorte (opcionales)'}
              {importStep === 'preview' && 'Revisa los usuarios antes de importar'}
              {importStep === 'creating' && 'Creando usuarios...'}
              {importStep === 'results' && 'Resultado de la importación'}
            </DialogDescription>
          </DialogHeader>

          {importStep === 'upload' && (
            <div className="space-y-4">
              {/* Org and Cohort selectors in step 1 */}
              {isCompanyAdmin ? (
                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <Input value={callerOrgName} disabled />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Empresa (opcional)</Label>
                  <Select value={importCompanyId || 'none'} onValueChange={(v) => { setImportCompanyId(v === 'none' ? '' : v); setImportCohortId(''); }}>
                    <SelectTrigger><SelectValue placeholder="Selecciona empresa" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin empresa</SelectItem>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>{org.name} {org.sector ? `- ${org.sector}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Cohorte (opcional)</Label>
                <Select value={importCohortId || 'none'} onValueChange={(v) => setImportCohortId(v === 'none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Selecciona cohorte" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {(() => {
                      const targetOrg = isCompanyAdmin ? callerOrgId : importCompanyId;
                      const filtered = targetOrg
                        ? cohorts.filter(c => c.is_public || !c.organization_id || c.organization_id === targetOrg)
                        : cohorts;
                      return filtered.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.program_name ? ` (${c.program_name})` : ''}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Rol para todos los usuarios importados</Label>
                <Select value={importRole} onValueChange={setImportRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participant">Participante</SelectItem>
                    {isSuperAdmin && <SelectItem value="admin">Admin Empresa</SelectItem>}
                    {!isCompanyAdmin && <SelectItem value="mentor">Mentor</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Se asignará el mismo rol a todos los usuarios del archivo.</p>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Los usuarios sin contraseña recibirán un email de invitación — si falla por límite de tasa, agrega contraseñas en el Excel.
                </AlertDescription>
              </Alert>

              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Arrastra un archivo o haz clic para seleccionar</p>
                <Input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="max-w-xs mx-auto" />
              </div>
              <div className="flex items-center justify-center">
                <Button variant="link" onClick={handleDownloadTemplate} className="gap-2">
                  <Download className="w-4 h-4" />
                  Descargar plantilla
                </Button>
              </div>
            </div>
          )}

          {importStep === 'preview' && (() => {
            const hasPosition = importedUsers.some((u) => u.position);
            const hasArea = importedUsers.some((u) => u.area || u.originalArea);
            const enterpriseCount = importedUsers.filter(u => u.has_enterprise_license && u.isValid).length;
            const proCount = importedUsers.filter(u => u.pro_license && u.isValid).length;
            const hasWarnings = importedUsers.some(u => u.warnings.length > 0);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-5 gap-3">
                  <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{importedUsers.length}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{validImportCount}</p><p className="text-xs text-muted-foreground">Válidos</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-destructive">{invalidImportCount}</p><p className="text-xs text-muted-foreground">Con errores</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-primary">{enterpriseCount}</p><p className="text-xs text-muted-foreground">Enterprise</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-cyan-600">{proCount}</p><p className="text-xs text-muted-foreground">Pro</p></CardContent></Card>
                </div>

                {invalidImportCount > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{invalidImportCount} registro(s) tienen errores y no serán importados</AlertDescription>
                  </Alert>
                )}

                {importQuotaWarnings.map((w, i) => (
                  <Alert key={i} variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{w}</AlertDescription>
                  </Alert>
                ))}

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Estado</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        {hasPosition && <TableHead>Cargo</TableHead>}
                        {hasArea && <TableHead>Área</TableHead>}
                        <TableHead>Licencia</TableHead>
                        <TableHead>Observaciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importedUsers.map((user, idx) => (
                        <TableRow key={idx} className={!user.isValid ? 'bg-destructive/5' : ''}>
                          <TableCell>{user.isValid ? <Check className="w-5 h-5 text-green-600" /> : <X className="w-5 h-5 text-destructive" />}</TableCell>
                          <TableCell className="font-medium">{user.full_name || '—'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell className="text-sm">{user.phone || <span className="text-destructive">falta</span>}</TableCell>
                          {hasPosition && <TableCell className="text-sm">{user.position || '—'}</TableCell>}
                          {hasArea && (
                            <TableCell className="text-sm">
                              {user.matchedArea ? (
                                <span>{user.matchedArea}</span>
                              ) : user.originalArea ? (
                                <span className="text-amber-600">{user.originalArea} ⚠</span>
                              ) : '—'}
                            </TableCell>
                          )}
                          <TableCell>
                            {user.has_enterprise_license ? (
                              <Badge variant="default" className="text-xs">Enterprise</Badge>
                            ) : user.pro_license ? (
                              <Badge variant="secondary" className="text-xs">
                                Pro{user.pro_end_date ? ` · hasta ${user.pro_end_date}` : ''}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {user.errors.length > 0 && <span className="text-sm text-destructive block">{user.errors.join(', ')}</span>}
                            {user.warnings.length > 0 && <span className="text-sm text-amber-600 block">{user.warnings.join(', ')}</span>}
                            {user.errors.length === 0 && user.warnings.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setImportedUsers([]); setImportStep('upload'); setImportQuotaWarnings([]); }}>Volver</Button>
                  <Button onClick={handleCreateImportedUsers} disabled={validImportCount === 0}>
                    {importQuotaWarnings.length > 0 ? `Importar de todas formas (${validImportCount})` : `Importar ${validImportCount} Usuarios`}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}

          {importStep === 'creating' && (
            <div className="py-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
              <p className="text-lg font-medium">Creando usuarios...</p>
              <p className="text-muted-foreground">Esto puede tomar unos segundos</p>
            </div>
          )}

          {importStep === 'results' && (
            <div className="space-y-4">
              {(() => {
                const successCount = importResults.filter((r) => r.success).length;
                const failCount = importResults.filter((r) => !r.success).length;
                return (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="p-4 text-center">
                          <Check className="w-8 h-8 mx-auto text-green-600 mb-2" />
                          <p className="text-2xl font-bold text-green-600">{successCount}</p>
                          <p className="text-sm text-muted-foreground">Creados exitosamente</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <X className="w-8 h-8 mx-auto text-destructive mb-2" />
                          <p className="text-2xl font-bold text-destructive">{failCount}</p>
                          <p className="text-sm text-muted-foreground">Con errores</p>
                        </CardContent>
                      </Card>
                    </div>
                    {failCount > 0 && (
                      <div className="border rounded-md max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Email</TableHead>
                              <TableHead>Error</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {importResults.filter((r) => !r.success).map((r, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm">{r.email}</TableCell>
                                <TableCell className="text-sm text-destructive">{r.error}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    <DialogFooter>
                      <Button onClick={() => {
                        setIsImportDialogOpen(false);
                        setImportedUsers([]);
                        setImportStep('upload');
                        setImportResults([]);
                        setImportCohortId('');
                        setImportRole('participant');
                        setImportQuotaWarnings([]);
                        setImportCompanyId(isCompanyAdmin ? (callerOrgId || '') : '');
                      }}>
                        Cerrar
                      </Button>
                    </DialogFooter>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === USER DETAIL DIALOG === */}
      <Dialog open={isUserDetailOpen} onOpenChange={setIsUserDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de Usuario</DialogTitle>
            <DialogDescription>Información completa del usuario</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-xl">
                  {selectedUser.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedUser.full_name}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {selectedUser.roles.map((r, i) => <span key={i}>{getRoleBadge(r)}</span>)}
                    {getStatusBadge(selectedUser.status)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Empresa</p>
                  <p className="font-medium">{selectedUser.organization_name || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Cohortes</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedUser.cohorts.length > 0
                      ? selectedUser.cohorts.map((c) => <Badge key={c.id} variant="outline" className="text-xs">{c.name}</Badge>)
                      : <span className="text-sm text-muted-foreground">Sin asignar</span>}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Fecha de registro</p>
                  <p className="font-medium">{format(new Date(selectedUser.created_at), 'dd MMMM yyyy', { locale: es })}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Último acceso</p>
                  <p className="font-medium">
                    {selectedUser.last_login
                      ? format(new Date(selectedUser.last_login), 'dd MMM yyyy, HH:mm', { locale: es })
                      : 'Nunca'}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Cargo</p>
                  <p className="font-medium">{selectedUser.position || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Área</p>
                  <p className="font-medium">{selectedUser.area || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Teléfono (WhatsApp)</p>
                  <p className="font-medium">{selectedUser.phone || '—'}</p>
                </div>
              </div>

              {isSuperAdmin && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Origen de registro
                  </h4>
                  {userUtmDetailError ? (
                    <p className="text-sm text-muted-foreground">
                      Información de origen no disponible
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm text-muted-foreground">Fuente</p>
                        <p className="font-medium">
                          {sourceLabel(
                            userUtmDetail
                              ? { source: userUtmDetail.utm_source, isDirect: userUtmDetail.is_direct }
                              : null
                          )}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm text-muted-foreground">Medio</p>
                        <p className="font-medium">{userUtmDetail?.utm_medium ?? '—'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm text-muted-foreground">Campaña</p>
                        <p className="font-medium">{userUtmDetail?.utm_campaign ?? '—'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm text-muted-foreground">Contenido</p>
                        <p className="font-medium">{userUtmDetail?.utm_content ?? '—'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm text-muted-foreground">Primera llegada</p>
                        <p className="font-medium">
                          {userUtmDetail
                            ? format(new Date(userUtmDetail.first_seen_at), 'dd MMM yyyy, HH:mm', { locale: es })
                            : '—'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario permanentemente?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará a <strong>{userToDelete?.full_name}</strong> ({userToDelete?.email}) de forma permanente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingUser}
            >
              {isDeletingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pro courtesy license dialog */}
      <Dialog
        open={!!proLicenseDialog}
        onOpenChange={(open) => { if (!open) { setProLicenseDialog(null); setProLicenseReason(''); setProLicenseStart(''); setProLicenseEnd(''); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {proLicenseDialog?.mode === 'grant' ? (
                <><Gift className="w-5 h-5 text-amber-600" /> Otorgar Licencia Pro de cortesía</>
              ) : (
                <><Crown className="w-5 h-5 text-amber-700" /> Revocar Licencia Pro de cortesía</>
              )}
            </DialogTitle>
            <DialogDescription>
              {proLicenseDialog?.mode === 'grant'
                ? `Activa una licencia Pro sin pago para ${proLicenseDialog?.user.full_name} (${proLicenseDialog?.user.email}). El usuario tendrá los mismos permisos que un suscriptor de Stripe.`
                : `Desactivará la licencia Pro de cortesía de ${proLicenseDialog?.user.full_name} (${proLicenseDialog?.user.email}). Las suscripciones pagadas en Stripe no se ven afectadas.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="pro-license-reason">
              Motivo / comentario {proLicenseDialog?.mode === 'grant' && <span className="text-destructive">*</span>}
            </Label>
            <Textarea
              id="pro-license-reason"
              placeholder={
                proLicenseDialog?.mode === 'grant'
                  ? 'Ej: Cortesía por alianza estratégica / pago realizado por transferencia bancaria / beneficio embajador...'
                  : 'Motivo de la revocación (opcional)'
              }
              value={proLicenseReason}
              onChange={(e) => setProLicenseReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              Quedará registrado en el log de auditoría junto al Super Admin que realiza la acción.
            </p>
            {proLicenseDialog?.mode === 'grant' && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <Label htmlFor="pro-license-start">Fecha inicio</Label>
                  <Input
                    id="pro-license-start"
                    type="date"
                    value={proLicenseStart}
                    onChange={(e) => setProLicenseStart(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Vacío = hoy</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pro-license-end">Fecha fin</Label>
                  <Input
                    id="pro-license-end"
                    type="date"
                    value={proLicenseEnd}
                    onChange={(e) => setProLicenseEnd(e.target.value)}
                    min={proLicenseStart || undefined}
                  />
                  <p className="text-xs text-muted-foreground">Vacío = ilimitada</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setProLicenseDialog(null); setProLicenseReason(''); setProLicenseStart(''); setProLicenseEnd(''); }}
              disabled={isTogglingProLicense}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmProLicense}
              disabled={isTogglingProLicense}
              className={proLicenseDialog?.mode === 'grant' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              variant={proLicenseDialog?.mode === 'revoke' ? 'destructive' : 'default'}
            >
              {isTogglingProLicense && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {proLicenseDialog?.mode === 'grant' ? 'Otorgar Pro' : 'Revocar Pro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Pro courtesy license dialog */}
      <Dialog
        open={!!bulkDialog}
        onOpenChange={(open) => { if (!open) { setBulkDialog(null); setBulkReason(''); setBulkStart(''); setBulkEnd(''); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkDialog?.mode === 'grant' ? (
                <><Gift className="w-5 h-5 text-amber-600" /> Otorgar Licencia Pro a {selectedIds.size} usuario(s)</>
              ) : (
                <><Crown className="w-5 h-5 text-amber-700" /> Revocar Licencia Pro a {selectedIds.size} usuario(s)</>
              )}
            </DialogTitle>
            <DialogDescription>
              {bulkDialog?.mode === 'grant'
                ? 'Activa una licencia Pro de cortesía (sin pago) para todos los usuarios seleccionados.'
                : 'Desactivará la licencia Pro de cortesía de los usuarios seleccionados. Las suscripciones pagadas en Stripe no se ven afectadas.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="bulk-pro-reason">
              Motivo / comentario {bulkDialog?.mode === 'grant' && <span className="text-destructive">*</span>}
            </Label>
            <Textarea
              id="bulk-pro-reason"
              placeholder={bulkDialog?.mode === 'grant' ? 'Ej: Cortesía por alianza estratégica...' : 'Motivo de la revocación (opcional)'}
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              rows={3}
              maxLength={500}
            />
            {bulkDialog?.mode === 'grant' && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <Label htmlFor="bulk-pro-start">Fecha inicio</Label>
                  <Input id="bulk-pro-start" type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} />
                  <p className="text-xs text-muted-foreground">Vacío = hoy</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bulk-pro-end">Fecha fin</Label>
                  <Input id="bulk-pro-end" type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} min={bulkStart || undefined} />
                  <p className="text-xs text-muted-foreground">Vacío = ilimitada</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDialog(null); setBulkReason(''); setBulkStart(''); setBulkEnd(''); }} disabled={isBulkTogglingProLicense}>
              Cancelar
            </Button>
            <Button
              onClick={handleBulkConfirm}
              disabled={isBulkTogglingProLicense}
              className={bulkDialog?.mode === 'grant' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              variant={bulkDialog?.mode === 'revoke' ? 'destructive' : 'default'}
            >
              {isBulkTogglingProLicense && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {bulkDialog?.mode === 'grant' ? 'Otorgar Pro' : 'Revocar Pro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk assign / remove organization dialog */}
      <Dialog
        open={!!orgBulkDialog}
        onOpenChange={(open) => { if (!open) { setOrgBulkDialog(null); setOrgBulkTarget(''); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {orgBulkDialog?.mode === 'assign'
                ? `Asignar empresa a ${selectedIds.size} usuario(s)`
                : `Quitar empresa a ${selectedIds.size} usuario(s)`}
            </DialogTitle>
            <DialogDescription>
              {orgBulkDialog?.mode === 'assign'
                ? 'Los usuarios seleccionados pasarán a pertenecer a esta empresa (reemplaza la empresa que tengan hoy).'
                : 'Los usuarios seleccionados quedarán sin empresa. Dejarán de verse en los módulos de su empresa actual y de recibir el plan de esa empresa.'}
            </DialogDescription>
          </DialogHeader>
          {orgBulkDialog?.mode === 'assign' && (
            <div className="space-y-2 py-2">
              <Label>Empresa</Label>
              <Select value={orgBulkTarget} onValueChange={setOrgBulkTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una empresa" />
                </SelectTrigger>
                <SelectContent>
                  {[...organizations].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            No cambia sus cohortes ni sus licencias. Queda registrado en el log de auditoría.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOrgBulkDialog(null); setOrgBulkTarget(''); }} disabled={isBulkSettingOrganization}>
              Cancelar
            </Button>
            <Button
              onClick={handleOrgBulkConfirm}
              disabled={isBulkSettingOrganization || (orgBulkDialog?.mode === 'assign' && !orgBulkTarget)}
              variant={orgBulkDialog?.mode === 'remove' ? 'destructive' : 'default'}
            >
              {isBulkSettingOrganization && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {orgBulkDialog?.mode === 'assign' ? 'Asignar empresa' : 'Quitar empresa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
